/**
 * @file utils/threadStorage.ts
 * 7.3-05 — Persistance locale du fil conversationnel (interim front).
 * 7.3-06 — Extension multi-conversations (liste type WhatsApp).
 *
 * Abstraction `ChatStore` : l'UI ne connaît que cette interface, ce qui
 * permettra de basculer vers la persistance serveur (API /channels, P1/P2)
 * sans refonte. Implémentation `LocalChatStore` : IndexedDB (idb-keyval)
 * avec repli localStorage si IndexedDB est indisponible ou échoue.
 *
 * Règles :
 *  - taille bornée (MAX_MESSAGES par conversation) pour le quota ;
 *  - quota plein → réduction puis abandon silencieux (pas de crash) ;
 *  - rien de classifié restreint ne doit être écrit : le filtrage des dossiers
 *    restreints est fait par l'appelant (useChatThread) AVANT save().
 */
import { get, set, del } from 'idb-keyval';
import type { ChatMessage } from '../components/chat/types.ts';

// ── Modèle persisté ──────────────────────────────────────────────────────────

export interface PersistedThread {
  /** Version du schéma de fil (compat 7.3-05). */
  v: 1;
  messages: ChatMessage[];
  activeDossierId: string | null;
  savedAt: number;
}

/** Conversation persistée (7.3-06). */
export interface StoredConversation {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  messages: ChatMessage[];
  activeDossierId: string | null;
  /** Messages agent arrivés alors que la conversation n'était pas ouverte. */
  unread: number;
}

/** Métadonnées exposées à l'UI (liste de conversations). */
export interface ConversationMeta {
  id: string;
  title: string;
  updatedAt: number;
  /** Aperçu du dernier message (tronqué). */
  lastPreview: string;
  unread: number;
}

interface PersistedWorkspace {
  v: 2;
  activeId: string | null;
  conversations: StoredConversation[];
}

/**
 * Interface de persistance du chat. Implémentations possibles :
 *  - LocalChatStore (ci-dessous) : 100% front, interim.
 *  - ServerChatStore (P1/P2) : adossée à l'API /channels du backend.
 *
 * Les méthodes load/save/clear (7.3-05) opèrent sur la conversation ACTIVE ;
 * les méthodes *Conversation* (7.3-06) gèrent la liste multi-fils.
 */
export interface ChatStore {
  load(): Promise<PersistedThread | null>;
  save(messages: ChatMessage[], activeDossierId: string | null): Promise<void>;
  /** Purge TOTALE (toutes conversations) — appelée à la déconnexion. */
  clear(): Promise<void>;

  listConversations(): Promise<ConversationMeta[]>;
  createConversation(title?: string): Promise<ConversationMeta>;
  renameConversation(id: string, title: string): Promise<void>;
  deleteConversation(id: string): Promise<void>;
  getActiveConversationId(): Promise<string | null>;
  setActiveConversation(id: string): Promise<void>;
  loadThread(id: string): Promise<PersistedThread | null>;
  saveThread(id: string, messages: ChatMessage[], activeDossierId: string | null): Promise<void>;
}

// ── Constantes & helpers ─────────────────────────────────────────────────────

const KEY_V1 = 'assistia.chat.thread.v1';
const KEY_V2 = 'assistia.chat.workspace.v2';
/** Borne dure : on ne garde que les N derniers messages par conversation. */
const MAX_MESSAGES = 200;
const PREVIEW_LEN = 80;

let convSeq = 0;
const newConvId = (): string => `c-${Date.now()}-${convSeq++}`;

function hasIndexedDB(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch {
    return false;
  }
}

function isPersistedThread(v: unknown): v is PersistedThread {
  if (typeof v !== 'object' || v === null) return false;
  const t = v as Partial<PersistedThread>;
  return t.v === 1 && Array.isArray(t.messages) && typeof t.savedAt === 'number';
}

function isWorkspace(v: unknown): v is PersistedWorkspace {
  if (typeof v !== 'object' || v === null) return false;
  const w = v as Partial<PersistedWorkspace>;
  return w.v === 2 && Array.isArray(w.conversations);
}

/**
 * Normalisation à l'hydratation : un upload interrompu par le reload ne doit
 * pas réafficher une barre de progression figée.
 */
function normalize(messages: ChatMessage[]): ChatMessage[] {
  return messages.map((m) =>
    m.kind === 'document' && m.progress < 100 ? { ...m, progress: 100 } : m,
  );
}

/** Aperçu lisible du dernier message d'une conversation. */
function previewOf(messages: ChatMessage[]): string {
  const last = messages[messages.length - 1];
  if (!last) return 'Conversation vide';
  let text: string;
  switch (last.kind) {
    case 'text':
      text = last.text;
      break;
    case 'document':
      text = `📄 ${last.fileName}`;
      break;
    case 'result':
      text = last.restricted ? 'Résultat (accès restreint)' : `Résultat : ${last.summary}`;
      break;
    case 'hitl':
      text = `Validation requise : ${last.text}`;
      break;
    default:
      text = '';
  }
  return text.length > PREVIEW_LEN ? `${text.slice(0, PREVIEW_LEN)}…` : text;
}

function toMeta(c: StoredConversation): ConversationMeta {
  return {
    id: c.id,
    title: c.title,
    updatedAt: c.updatedAt,
    lastPreview: previewOf(c.messages),
    unread: c.unread,
  };
}

function freshConversation(title?: string): StoredConversation {
  const now = Date.now();
  return {
    id: newConvId(),
    title: title ?? 'Nouvelle conversation',
    createdAt: now,
    updatedAt: now,
    messages: [],
    activeDossierId: null,
    unread: 0,
  };
}

// ── Implémentation locale (IndexedDB → repli localStorage) ───────────────────

class LocalChatStore implements ChatStore {
  private readonly idb = hasIndexedDB();

  // ── Couche bas niveau : lecture/écriture du workspace entier ──────────────

  private async readRaw(key: string): Promise<unknown> {
    if (this.idb) {
      try {
        const raw = await get<unknown>(key);
        if (raw !== undefined && raw !== null) return raw;
      } catch {
        /* IndexedDB en échec → repli localStorage. */
      }
    }
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as unknown) : null;
    } catch {
      return null;
    }
  }

  private async writeRaw(key: string, payload: unknown): Promise<void> {
    if (this.idb) {
      try {
        await set(key, payload);
        return;
      } catch {
        /* Quota IndexedDB ou échec → repli localStorage ci-dessous. */
      }
    }
    try {
      localStorage.setItem(key, JSON.stringify(payload));
    } catch {
      /* Quota plein : géré par l'appelant (réduction) ou abandon silencieux. */
    }
  }

  private async deleteRaw(key: string): Promise<void> {
    if (this.idb) {
      try {
        await del(key);
      } catch {
        /* ignore */
      }
    }
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }

  /**
   * Charge le workspace v2 ; migre l'éventuel fil v1 (7.3-05) en première
   * conversation ; garantit qu'au moins une conversation existe.
   */
  private async loadWorkspace(): Promise<PersistedWorkspace> {
    const raw = await this.readRaw(KEY_V2);
    if (isWorkspace(raw) && raw.conversations.length > 0) return raw;

    // Migration v1 → v2 : l'ancien fil unique devient la première conversation.
    const legacy = await this.readRaw(KEY_V1);
    let first: StoredConversation;
    if (isPersistedThread(legacy)) {
      first = {
        ...freshConversation('Conversation'),
        messages: legacy.messages,
        activeDossierId: legacy.activeDossierId ?? null,
        updatedAt: legacy.savedAt,
      };
      await this.deleteRaw(KEY_V1);
    } else {
      first = freshConversation();
    }
    const ws: PersistedWorkspace = { v: 2, activeId: first.id, conversations: [first] };
    await this.writeRaw(KEY_V2, ws);
    return ws;
  }

  private async saveWorkspace(ws: PersistedWorkspace): Promise<void> {
    await this.writeRaw(KEY_V2, ws);
  }

  // ── 7.3-05 : fil actif ─────────────────────────────────────────────────────

  async load(): Promise<PersistedThread | null> {
    const ws = await this.loadWorkspace();
    const active = ws.conversations.find((c) => c.id === ws.activeId) ?? ws.conversations[0];
    if (!active) return null;
    return {
      v: 1,
      messages: normalize(active.messages),
      activeDossierId: active.activeDossierId,
      savedAt: active.updatedAt,
    };
  }

  async save(messages: ChatMessage[], activeDossierId: string | null): Promise<void> {
    const ws = await this.loadWorkspace();
    const id = ws.activeId ?? ws.conversations[0]?.id;
    if (!id) return;
    await this.saveThread(id, messages, activeDossierId);
  }

  async clear(): Promise<void> {
    await this.deleteRaw(KEY_V2);
    await this.deleteRaw(KEY_V1);
  }

  // ── 7.3-06 : multi-conversations ───────────────────────────────────────────

  async listConversations(): Promise<ConversationMeta[]> {
    const ws = await this.loadWorkspace();
    return [...ws.conversations]
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .map(toMeta);
  }

  async createConversation(title?: string): Promise<ConversationMeta> {
    const ws = await this.loadWorkspace();
    const count = ws.conversations.length + 1;
    const conv = freshConversation(title ?? `Conversation ${count}`);
    ws.conversations.push(conv);
    ws.activeId = conv.id;
    await this.saveWorkspace(ws);
    return toMeta(conv);
  }

  async renameConversation(id: string, title: string): Promise<void> {
    const ws = await this.loadWorkspace();
    const conv = ws.conversations.find((c) => c.id === id);
    if (!conv) return;
    conv.title = title.trim() || conv.title;
    conv.updatedAt = Date.now();
    await this.saveWorkspace(ws);
  }

  async deleteConversation(id: string): Promise<void> {
    const ws = await this.loadWorkspace();
    ws.conversations = ws.conversations.filter((c) => c.id !== id);
    if (ws.conversations.length === 0) {
      const fresh = freshConversation();
      ws.conversations.push(fresh);
      ws.activeId = fresh.id;
    } else if (ws.activeId === id) {
      // Bascule sur la conversation la plus récente restante.
      const next = [...ws.conversations].sort((a, b) => b.updatedAt - a.updatedAt)[0];
      ws.activeId = next?.id ?? null;
    }
    await this.saveWorkspace(ws);
  }

  async getActiveConversationId(): Promise<string | null> {
    const ws = await this.loadWorkspace();
    return ws.activeId ?? ws.conversations[0]?.id ?? null;
  }

  async setActiveConversation(id: string): Promise<void> {
    const ws = await this.loadWorkspace();
    const conv = ws.conversations.find((c) => c.id === id);
    if (!conv) return;
    ws.activeId = id;
    conv.unread = 0; // ouverture → lu
    await this.saveWorkspace(ws);
  }

  async loadThread(id: string): Promise<PersistedThread | null> {
    const ws = await this.loadWorkspace();
    const conv = ws.conversations.find((c) => c.id === id);
    if (!conv) return null;
    return {
      v: 1,
      messages: normalize(conv.messages),
      activeDossierId: conv.activeDossierId,
      savedAt: conv.updatedAt,
    };
  }

  async saveThread(
    id: string,
    messages: ChatMessage[],
    activeDossierId: string | null,
  ): Promise<void> {
    const ws = await this.loadWorkspace();
    const conv = ws.conversations.find((c) => c.id === id);
    if (!conv) return;
    const bounded = messages.slice(-MAX_MESSAGES);
    // Pastille non-lus : messages agent arrivés sur une conversation inactive.
    if (ws.activeId !== id) {
      const added = Math.max(0, bounded.length - conv.messages.length);
      conv.unread += added;
    }
    conv.messages = bounded;
    conv.activeDossierId = activeDossierId;
    conv.updatedAt = Date.now();
    try {
      await this.saveWorkspace(ws);
    } catch {
      // Quota plein : on retente avec moitié moins de messages, puis abandon.
      conv.messages = bounded.slice(-Math.floor(MAX_MESSAGES / 2));
      try {
        await this.saveWorkspace(ws);
      } catch {
        /* Stockage indisponible — le fil restera en mémoire uniquement. */
      }
    }
  }
}

/** Store par défaut de l'app (swap possible vers une implémentation serveur). */
export const chatStore: ChatStore = new LocalChatStore();

/** Purge complète du stockage local du chat (appelée à la déconnexion). */
export async function clearChatStorage(): Promise<void> {
  await chatStore.clear();
}
